import React, { useMemo, useState, useCallback } from "react";
import { normListId } from "../../common/dualListBox";
import {
  DEFAULT_STUDY_COLOR,
  pickStudyColorRaw,
  resolveStudyDisplayColor,
} from "../../../utils/studyColorUtils";
import "./StudyAssignmentDualList.scss";

function pickStudyId(study) {
  return study?.StudyId ?? study?.value ?? study?.StudyID ?? "";
}

function parseStudyLabelParts(label = "") {
  const text = String(label || "").trim();
  const dash = text.indexOf(" - ");
  if (dash > 0) {
    return {
      number: text.slice(0, dash).trim(),
      name: text.slice(dash + 3).trim(),
    };
  }
  return { number: text, name: "" };
}

function findStudyOption(options, studyId) {
  const id = normListId(studyId);
  return (options || []).find((opt) => normListId(opt?.value) === id) || null;
}

function resolveStudyMeta(row, options = []) {
  const option = findStudyOption(options, pickStudyId(row));
  const parsed = parseStudyLabelParts(row?.label || option?.label || "");
  const number =
    row?.studyNumber || option?.studyNumber || parsed.number || "Study";
  const name = row?.studyName || option?.studyName || parsed.name || "";
  const label =
    row?.label ||
    option?.label ||
    (name ? `${number} - ${name}` : number);
  const rawColor =
    pickStudyColorRaw(row) ||
    pickStudyColorRaw(option) ||
    pickStudyColorRaw({ studyColor: row?.studyColor });
  const color = resolveStudyDisplayColor(rawColor, DEFAULT_STUDY_COLOR);

  return { number, name, label, color, rawColor };
}

function buildStudyRow(option, fallbackId) {
  if (!option && !fallbackId) return null;
  const meta = resolveStudyMeta(
    {
      StudyId: option?.value || fallbackId,
      label: option?.label,
      studyNumber: option?.studyNumber,
      studyName: option?.studyName,
      StudySchColor: option?.StudySchColor,
      StudyColor: option?.StudyColor,
      studyColor: option?.studyColor,
    },
    option ? [option] : [],
  );

  return {
    StudyId: option?.value || fallbackId,
    label: meta.label,
    studyNumber: meta.number,
    studyName: meta.name,
    studyColor: meta.rawColor,
    StudySchColor: pickStudyColorRaw(option) || meta.rawColor,
    StudyColor: option?.StudyColor || meta.rawColor,
  };
}

/** Map org study catalog options → assigned-study rows. */
export function mapStudyOptionsToAssignedRows(options = []) {
  return (options || [])
    .map((option) => buildStudyRow(option, option?.value))
    .filter(Boolean);
}

function StudyRow({ row, options, onClick, disabled, actionHint }) {
  const meta = resolveStudyMeta(row, options);
  const displayName = meta.name || meta.label;

  return (
    <button
      type="button"
      className="study-assignment-dual__item"
      onClick={onClick}
      disabled={disabled}
      title={`${meta.label}${actionHint ? ` — ${actionHint}` : ""}`}
    >
      <span className="study-assignment-dual__badge">
        <span
          className="study-assignment-dual__dot"
          style={{ backgroundColor: meta.color }}
          aria-hidden="true"
        />
        <span className="study-assignment-dual__code">{meta.number}</span>
      </span>
      <span className="study-assignment-dual__name">{displayName}</span>
    </button>
  );
}

/**
 * Click-to-move dual list for assigning studies to a user.
 * Click Available → moves to Selected; click Selected → moves back.
 *
 * @param {object[]} options - Org study catalog (`value`, `label`, studyNumber, …)
 * @param {object[]} selected - Assigned rows (`StudyId`, `label`, …)
 * @param {(rows: object[]) => void} [onChange]
 * @param {boolean} [readOnly]
 * @param {string} [readOnlyMessage]
 * @param {string} [availableTitle]
 * @param {string} [selectedTitle]
 */
function StudyAssignmentDualList({
  options = [],
  selected = [],
  onChange,
  readOnly = false,
  readOnlyMessage = "System Administrator has access to all studies (read-only).",
  availableTitle = "Available",
  selectedTitle = "Selected",
  emptyAvailableMessage = "No studies available.",
  emptySelectedMessage = "No studies selected yet.",
}) {
  const [availableSearch, setAvailableSearch] = useState("");
  const [selectedSearch, setSelectedSearch] = useState("");

  const selectedRows = useMemo(() => {
    const seen = new Set();
    return (selected || [])
      .map((row) => {
        const id = pickStudyId(row);
        const key = normListId(id);
        if (!key || seen.has(key)) return null;
        seen.add(key);
        const option = findStudyOption(options, id);
        const built = buildStudyRow(option, id);
        if (!built) return null;
        return {
          ...built,
          ...row,
          StudyId: id || built.StudyId,
          label: row?.label || built.label,
          studyNumber: row?.studyNumber || built.studyNumber,
          studyName: row?.studyName || built.studyName,
          studyColor:
            pickStudyColorRaw(row) ||
            pickStudyColorRaw(option) ||
            built.studyColor,
          StudySchColor:
            pickStudyColorRaw(row) ||
            pickStudyColorRaw(option) ||
            built.StudySchColor,
        };
      })
      .filter(Boolean);
  }, [options, selected]);

  const selectedIdSet = useMemo(
    () =>
      new Set(
        selectedRows.map((row) => normListId(pickStudyId(row))).filter(Boolean),
      ),
    [selectedRows],
  );

  const availableRows = useMemo(() => {
    return (options || [])
      .map((option) => buildStudyRow(option, option?.value))
      .filter(
        (row) => row && !selectedIdSet.has(normListId(pickStudyId(row))),
      );
  }, [options, selectedIdSet]);

  const filterRows = useCallback((rows, query) => {
    const q = String(query || "")
      .trim()
      .toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const meta = resolveStudyMeta(row, options);
      return `${meta.number} ${meta.name} ${meta.label}`
        .toLowerCase()
        .includes(q);
    });
  }, [options]);

  const filteredAvailable = useMemo(
    () => filterRows(availableRows, availableSearch),
    [availableRows, availableSearch, filterRows],
  );

  const filteredSelected = useMemo(
    () => filterRows(selectedRows, selectedSearch),
    [selectedRows, selectedSearch, filterRows],
  );

  const emitChange = useCallback(
    (nextRows) => {
      if (readOnly) return;
      onChange?.(nextRows);
    },
    [onChange, readOnly],
  );

  const moveToSelected = useCallback(
    (row) => {
      const id = normListId(pickStudyId(row));
      if (!id || selectedIdSet.has(id)) return;
      emitChange([...selectedRows, buildStudyRow(findStudyOption(options, pickStudyId(row)), pickStudyId(row))].filter(Boolean));
    },
    [emitChange, options, selectedIdSet, selectedRows],
  );

  const moveToAvailable = useCallback(
    (row) => {
      const id = normListId(pickStudyId(row));
      if (!id) return;
      emitChange(
        selectedRows.filter((item) => normListId(pickStudyId(item)) !== id),
      );
    },
    [emitChange, selectedRows],
  );

  const moveAllToSelected = useCallback(() => {
    if (!availableRows.length) return;
    emitChange([...selectedRows, ...availableRows]);
  }, [availableRows, emitChange, selectedRows]);

  const removeAll = useCallback(() => {
    if (!selectedRows.length) return;
    emitChange([]);
  }, [emitChange, selectedRows]);

  return (
    <div
      className={`study-assignment-dual${
        readOnly ? " study-assignment-dual--readonly" : ""
      }`}
    >
      {readOnly ? (
        <p className="study-assignment-dual__notice">{readOnlyMessage}</p>
      ) : null}

      <div className="study-assignment-dual__panels">
        <section className="study-assignment-dual__panel">
          <header className="study-assignment-dual__header">
            <strong>{availableTitle}</strong>
            <span>{availableRows.length}</span>
          </header>
          <input
            type="search"
            className="study-assignment-dual__search"
            placeholder="Search..."
            value={availableSearch}
            onChange={(e) => setAvailableSearch(e.target.value)}
            disabled={readOnly}
            aria-label="Search available studies"
          />
          {!readOnly ? (
            <div className="study-assignment-dual__actions">
              <button
                type="button"
                className="study-assignment-dual__bulk study-assignment-dual__bulk--add"
                onClick={moveAllToSelected}
                disabled={!availableRows.length}
              >
                Assign All →
              </button>
            </div>
          ) : (
            <div className="study-assignment-dual__actions" aria-hidden="true" />
          )}
          <div className="study-assignment-dual__list" role="listbox">
            {options.length === 0 ? (
              <p className="study-assignment-dual__empty">{emptyAvailableMessage}</p>
            ) : filteredAvailable.length === 0 ? (
              <p className="study-assignment-dual__empty">
                {availableRows.length === 0
                  ? "All studies selected."
                  : "No matching studies."}
              </p>
            ) : (
              filteredAvailable.map((row) => (
                <StudyRow
                  key={normListId(pickStudyId(row)) || pickStudyId(row)}
                  row={row}
                  options={options}
                  disabled={readOnly}
                  actionHint="click to select"
                  onClick={() => moveToSelected(row)}
                />
              ))
            )}
          </div>
        </section>

        <section className="study-assignment-dual__panel">
          <header className="study-assignment-dual__header">
            <strong>{selectedTitle}</strong>
            <span>{selectedRows.length}</span>
          </header>
          <input
            type="search"
            className="study-assignment-dual__search"
            placeholder="Search..."
            value={selectedSearch}
            onChange={(e) => setSelectedSearch(e.target.value)}
            disabled={readOnly}
            aria-label="Search selected studies"
          />
          {!readOnly ? (
            <div className="study-assignment-dual__actions">
              <button
                type="button"
                className="study-assignment-dual__bulk study-assignment-dual__bulk--remove"
                onClick={removeAll}
                disabled={!selectedRows.length}
              >
                ← Remove All
              </button>
            </div>
          ) : (
            <div className="study-assignment-dual__actions" aria-hidden="true" />
          )}
          <div className="study-assignment-dual__list" role="listbox">
            {filteredSelected.length === 0 ? (
              <p className="study-assignment-dual__empty">
                {selectedRows.length === 0
                  ? emptySelectedMessage
                  : "No matching studies."}
              </p>
            ) : (
              filteredSelected.map((row) => (
                <StudyRow
                  key={normListId(pickStudyId(row)) || pickStudyId(row)}
                  row={row}
                  options={options}
                  disabled={readOnly}
                  actionHint="click to remove"
                  onClick={() => moveToAvailable(row)}
                />
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default StudyAssignmentDualList;
